import {
  LANGUAGE_COOKIE_NAME,
  CURRENCY_COOKIE_NAME,
  SESSION_ID_COOKIE_NAME,
  CLIENT_ID_COOKIE_NAME,
  TRACK_ID_COOKIE_NAME,
} from "@holisto-web/cookies";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useCookies } from "react-cookie";
import Cookies from "universal-cookie";
import { setSentryTag } from "./set-sentry-tag.js";
import { getSessionTraceId } from "@holisto-web/session-trace-id";

type CookieReader = (name: string) => string | undefined;

type SentryCookieContext = {
  tags: Record<string, string>;
  user: { id: string } | undefined;
};

const SENTRY_COOKIE_TAG_NAMES: Record<string, string> = {
  [LANGUAGE_COOKIE_NAME]: "languageCode",
  [CURRENCY_COOKIE_NAME]: "currencyCode",
  [SESSION_ID_COOKIE_NAME]: "sessionId",
  [CLIENT_ID_COOKIE_NAME]: "clientId",
  [TRACK_ID_COOKIE_NAME]: "trackId",
};
const sentryCookieNames = Object.keys(SENTRY_COOKIE_TAG_NAMES);

export const createSentryCookieContext = (getCookie: CookieReader): SentryCookieContext => {
  const tags: Record<string, string> = {};

  for (const [cookieName, tagName] of Object.entries(SENTRY_COOKIE_TAG_NAMES)) {
    const cookieValue = getCookie(cookieName);
    if (cookieValue) {
      tags[tagName] = cookieValue;
    }
  }

  const clientId = getCookie(CLIENT_ID_COOKIE_NAME);

  return {
    tags: tags,
    user: clientId ? { id: clientId } : undefined,
  };
};

export const getSentryCookieContext = (): SentryCookieContext => {
  const cookies = new Cookies();
  return createSentryCookieContext((name) => {
    const cookieValue = cookies.get(name);
    return typeof cookieValue === "string" ? cookieValue : undefined;
  });
};

export const syncSentryCookieContext = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  const { tags, user } = getSentryCookieContext();
  for (const tagName of Object.values(SENTRY_COOKIE_TAG_NAMES)) {
    setSentryTag(tagName, tags[tagName]);
  }
  setSentryTag("sessionTraceId", getSessionTraceId(window.SESSION_TRACE_ID));
  Sentry.setUser(user ?? null);
};

export const useSentryCookieContextSynchronization = (): void => {
  const [cookies] = useCookies(sentryCookieNames);

  useEffect(() => {
    syncSentryCookieContext();
  }, [cookies]);
};
